import React from "react";
import { Paragraph } from "@arctheme-components/elements/Paragraph/Paragraph";

interface MinicartTotalsProps {
  cart_subtotal: number;
  cart_items: any[];
  settings: {
    show_subtotal?: boolean;
    show_savings?: boolean;
    show_shipping_note?: boolean;
    subtotal_label_style?: string;
    subtotal_value_style?: string;
    savings_label_style?: string;
    savings_value_style?: string;
    total_label_style?: string;
    total_value_style?: string;
    subtotal_label_text?: string;
    savings_label_text?: string;
    total_label_text?: string;
    shipping_note_style?: string;
    shipping_note_text?: string;
  };
}

export const MinicartTotals = ({ cart_subtotal, cart_items, settings }: MinicartTotalsProps) => {
    if (!cart_items?.length) return null;

    // Apply settings with defaults
    const showSubtotal = settings?.show_subtotal !== false;
    const showSavings = settings?.show_savings !== false;
    const showShippingNote = settings?.show_shipping_note !== false;
    const subtotalLabelStyle = settings?.subtotal_label_style || 'h5';
    const subtotalValueStyle = settings?.subtotal_value_style || subtotalLabelStyle;
    const savingsLabelStyle = settings?.savings_label_style || subtotalLabelStyle;
    const savingsValueStyle = settings?.savings_value_style || 'text-sm';
    const shippingNoteStyle = settings?.shipping_note_style || 'text-sm';
    const totalLabelStyle = settings?.total_label_style || 'h4';
    const totalValueStyle = settings?.total_value_style || totalLabelStyle;
    
    // Custom labels
    const subtotalLabelText = settings?.subtotal_label_text || 'SUBTOTAL';
    const savingsLabelText = settings?.savings_label_text || 'TOTAL SAVINGS';
    const totalLabelText = settings?.total_label_text || 'TOTAL';
    const shippingNoteText = settings?.shipping_note_text || 'Shipping, taxes, Gift cards & Promo codes calculated at checkout';

    // Calculate total savings for items with a selling plan or discounts
    const totalSavings = cart_items.reduce((acc, item) => {
      if (item.selling_plan_allocation) {
        // Subscription savings
        const originalPrice = item.selling_plan_allocation.compare_at_price;
        const discountedPrice = item.selling_plan_allocation.price;
        const itemSavings = ((originalPrice - discountedPrice) / 100) * item.quantity;
        return acc + itemSavings;
      } else if (item.line_level_discount_allocations?.length) {
        // Discount savings
        let itemDiscount = 0;
        item.line_level_discount_allocations.forEach(discount => {
          itemDiscount += discount.amount;
        });
        const itemSavings = itemDiscount / 100;
        return acc + itemSavings;
      } else if (item.product?.compare_at_price) {
        // Compare at price savings
        const originalPrice = item.product.compare_at_price;
        const discountedPrice = item.price;
        const itemSavings = ((originalPrice - discountedPrice) / 100) * item.quantity;
        return acc + itemSavings;
      }
      return acc;
    }, 0);

    const totalBeforeSavings = totalSavings + cart_subtotal;
    const hasSavings = totalSavings > 0;
    const currencySymbol = window.Alpine?.store('currency').getCurrencySymbol();

    return (
      <div className="minicart__totals flex-col w-full bg-[#FFF9F5] py-4 px-4 rounded-xl">
        {/* Subtotal Row */}
        {showSubtotal && (
          <div className="minicart__totals-row w-full flex justify-between items-center pb-3">
            <Paragraph classes={subtotalLabelStyle} text={subtotalLabelText} />
            <Paragraph 
              classes={subtotalValueStyle} 
              text={`${currencySymbol}${totalBeforeSavings.toFixed(2)}`} 
            />
          </div>
        )}
        
        {/* Savings Row */}
        {showSavings && hasSavings && (
          <div className="minicart__totals-row  w-full flex justify-between items-center  pb-3">
            <Paragraph classes={savingsLabelStyle} text={savingsLabelText} />
            <div className="">
              <Paragraph 
                classes={savingsValueStyle} 
                text={`-${currencySymbol}${totalSavings.toFixed(2)}`} 
              />
            </div>
          </div>
        )}
          {showShippingNote && (
          <div className="minicart__totals-row w-full flex justify-between items-center pb-3">
            <Paragraph classes={shippingNoteStyle} text={shippingNoteText} />
            
          </div>
        )}
        
        {/* Total Row */}
        <div className="minicart__totals-row flex  w-full justify-between items-center mt-1">
          <Paragraph classes={totalLabelStyle} text={totalLabelText} />
          <Paragraph 
            classes={totalValueStyle} 
            text={`${currencySymbol}${cart_subtotal.toFixed(2)}`} 
          />
        </div>
      </div>
    );
};